import type { ActionInput } from "./ActionInput.js";
import { calculateArrivalActions } from "./actions/arrival.js";
import {
  calculateEndIncidentActions,
  type EndIncidentAction,
} from "./actions/endIncident.js";
import { calculateFormDetailsActions } from "./actions/formDetails.js";
import { calculateMcaActions } from "./actions/mentalCapacityAct.js";
import { calculatePdfActions } from "./actions/pdf.js";
import { calculatePlaceOfSafetyActions } from "./actions/placeOfSafety.js";
import {
  calculatePolicePresenceActions,
  type MarkPolicePresenceAction,
} from "./actions/policePresenceRequests.js";
import { calculateSection136Actions } from "./actions/section136.js";
import { calculateShareActions } from "./actions/share.js";
import { calculateTransferActions } from "./actions/transfer.js";
import { calculateTriageActions } from "./actions/triage.js";

export * from "./helpers/index.js";

export const defaultActions: IncidentWorkItemActionsObject = {
  transfers: {
    canInitiate: false,
    canRevoke: false,
    canAcceptReject: false,
  },
  section136: {
    canStart: false,
    canEdit: false,
  },
  mentalCapacityAct: {
    canStart: false,
    canEdit: false,
  },
  endIncident: {
    canEnd: false,
    endIncidentType: "preTriageEndIncident",
  },
  triage: {
    canStart: false,
    canEdit: false,
  },
  placeOfSafety: {
    canAdd: false,
    canEdit: false,
  },
  arrival: {
    canMarkArrival: false,
    hasArrivedAtLatestPlaceOfSafety: false,
  },
  share: {
    canShareForm: false,
    hasSharedForm: false,
  },
  policePresence: {
    canEnterOfficerRequestInformation: false,
    formsRequiringPoliceResponse: [],
    completedPolicePresenceForms: [],
    policeCanRespond: false,
  },
  formDetails: {
    canAdd: false,
  },
  pdf: {
    canView: false,
    canDownload: false,
    canDownloadAsSupervisor: false,
  },
};

/**
 * Computes available actions for an incident work item.
 *
 * Each property is lazily evaluated on first access and cached for subsequent
 * reads. This avoids the cost of computing all 11 action groups when callers
 * typically only inspect 1–3 of them (each group involves multiple Zod schema
 * builds and validations).
 */
export function calculateActions(
  actionInput: ActionInput,
): IncidentWorkItemActionsObject {
  const cache = new Map<string, unknown>();

  function lazy<T>(key: string, compute: () => T): T {
    if (!cache.has(key)) {
      cache.set(key, compute());
    }
    return cache.get(key) as T;
  }

  return {
    get transfers() {
      return lazy("transfers", () => calculateTransferActions(actionInput));
    },
    get section136() {
      return lazy("section136", () => calculateSection136Actions(actionInput));
    },
    get mentalCapacityAct() {
      return lazy("mentalCapacityAct", () => calculateMcaActions(actionInput));
    },
    get endIncident() {
      return lazy("endIncident", () =>
        calculateEndIncidentActions(actionInput),
      );
    },
    get triage() {
      return lazy("triage", () => calculateTriageActions(actionInput));
    },
    get placeOfSafety() {
      return lazy("placeOfSafety", () =>
        calculatePlaceOfSafetyActions(actionInput),
      );
    },
    get arrival() {
      return lazy("arrival", () => calculateArrivalActions(actionInput));
    },
    get share() {
      return lazy("share", () => calculateShareActions(actionInput));
    },
    get policePresence() {
      return lazy("policePresence", () =>
        calculatePolicePresenceActions(actionInput),
      );
    },
    get formDetails() {
      return lazy("formDetails", () =>
        calculateFormDetailsActions(actionInput),
      );
    },
    get pdf() {
      return lazy("pdf", () => calculatePdfActions(actionInput));
    },
  };
}

export type IncidentWorkItemActionsObject = {
  transfers: {
    canInitiate: boolean;
    canRevoke: boolean;
    canAcceptReject: boolean;
  };
  section136: {
    canStart: boolean;
    canEdit: boolean;
  };
  mentalCapacityAct: {
    canStart: boolean;
    canEdit: boolean;
  };
  endIncident: EndIncidentAction;
  triage: {
    canStart: boolean;
    canEdit: boolean;
  };
  placeOfSafety: {
    canAdd: boolean;
    canEdit: boolean;
  };
  arrival: {
    canMarkArrival: boolean;
    hasArrivedAtLatestPlaceOfSafety: boolean;
  };
  share: {
    canShareForm: boolean;
    hasSharedForm: boolean;
  };
  policePresence: MarkPolicePresenceAction;
  formDetails: {
    canAdd: boolean;
  };
  pdf: {
    canView: boolean;
    canDownload: boolean;
    canDownloadAsSupervisor: boolean;
  };
};

export * from "./actions/policePresenceUpdateDue.js";
export { calculateEndIncidentActions, type EndIncidentAction };
export {
  endIncidentDestination,
  type EndIncidentDestination,
} from "./actions/endIncident.js";
